import {
  IsArray,
  IsEmail,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  MaxLength,
} from 'class-validator'
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import {
  MAX_ESTABLISHMENT_NAME_LENGTH,
  MAX_SHORT_NAME_LENGTH,
  UAI_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_PHONE_LENGTH,
  MAX_STREET_LENGTH,
  MAX_CITY_LENGTH,
  MAX_POSTAL_CODE_LENGTH,
} from '../../../constants/establishment.constants'
import {
  EstablishmentCategory,
  EstablishmentStatus,
} from '../../../enums/content.enum'

const toNumber = ({ value }: { value: any }) => {
  if (value === '' || value === null || value === undefined) return undefined
  return Number(value)
}

export class CreateEstablishmentDto {
  @ApiProperty({
    description: "Nom de l'établissement",
    example: 'Lycée Victor Hugo',
    type: 'string',
  })
  @IsNotEmpty()
  @IsString()
  @MaxLength(MAX_ESTABLISHMENT_NAME_LENGTH)
  name!: string

  @ApiPropertyOptional({ description: 'Nom court', type: 'string' })
  @IsOptional()
  @IsString()
  @MaxLength(MAX_SHORT_NAME_LENGTH)
  shortName?: string

  @ApiProperty({ description: 'Code UAI', example: '0751234A', type: 'string' })
  @IsNotEmpty()
  @IsString()
  @MaxLength(UAI_LENGTH)
  uai!: string

  @ApiProperty({
    description: "ID du type d'établissement",
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty()
  @IsUUID('4')
  typeId!: string

  @ApiProperty({ description: 'Catégorie' })
  @IsNotEmpty()
  @IsString()
  category!: EstablishmentCategory

  @ApiPropertyOptional({ description: 'Statut' })
  @IsOptional()
  @IsString()
  status?: EstablishmentStatus

  @ApiProperty({ description: 'Filières', type: [String] })
  @IsNotEmpty()
  @IsArray()
  streams!: string[]

  @ApiProperty({ description: 'Email', type: 'string' })
  @IsNotEmpty()
  @IsEmail()
  @MaxLength(MAX_EMAIL_LENGTH)
  email!: string

  @ApiProperty({ description: 'Téléphone', type: 'string' })
  @IsNotEmpty()
  @IsString()
  @MaxLength(MAX_PHONE_LENGTH)
  phone!: string

  @ApiPropertyOptional({ description: 'Site web', type: 'string' })
  @IsOptional()
  @IsString()
  website?: string

  @ApiProperty({ description: 'Rue', type: 'string' })
  @IsNotEmpty()
  @IsString()
  @MaxLength(MAX_STREET_LENGTH)
  street!: string

  @ApiPropertyOptional({ description: "Complément d'adresse", type: 'string' })
  @IsOptional()
  @IsString()
  addressComplement?: string

  @ApiProperty({ description: 'Ville', type: 'string' })
  @IsNotEmpty()
  @IsString()
  @MaxLength(MAX_CITY_LENGTH)
  city!: string

  @ApiProperty({ description: 'Code postal', type: 'string' })
  @IsNotEmpty()
  @IsString()
  @MaxLength(MAX_POSTAL_CODE_LENGTH)
  postalCode!: string

  @ApiProperty({ description: 'Département', type: 'string' })
  @IsNotEmpty()
  @IsString()
  department!: string

  @ApiProperty({ description: 'Région', type: 'string' })
  @IsNotEmpty()
  @IsString()
  region!: string

  @ApiProperty({ description: 'Académie', type: 'string' })
  @IsNotEmpty()
  @IsString()
  academy!: string

  // --- Nouveaux champs enrichis ---

  @ApiPropertyOptional({ description: 'Code nature', type: 'string' })
  @IsOptional()
  @IsString()
  natureCode?: string

  @ApiPropertyOptional({ description: 'Libellé nature', type: 'string' })
  @IsOptional()
  @IsString()
  natureLabel?: string

  @ApiPropertyOptional({ description: 'A une SEGPA', type: Boolean })
  @IsOptional()
  hasSegpa?: boolean

  @ApiPropertyOptional({ description: 'A une ULIS', type: Boolean })
  @IsOptional()
  hasUlis?: boolean

  @ApiPropertyOptional({ description: 'Section Arts', type: Boolean })
  @IsOptional()
  sectionArts?: boolean

  @ApiPropertyOptional({ description: 'Section Cinéma', type: Boolean })
  @IsOptional()
  sectionCinema?: boolean

  @ApiPropertyOptional({ description: 'Section Théâtre', type: Boolean })
  @IsOptional()
  sectionTheatre?: boolean

  @ApiPropertyOptional({ description: 'Section Sport', type: Boolean })
  @IsOptional()
  sectionSport?: boolean

  @ApiPropertyOptional({ description: 'Section Internat', type: Boolean })
  @IsOptional()
  sectionInternat?: boolean

  @ApiPropertyOptional({ description: 'Section Internationale', type: Boolean })
  @IsOptional()
  sectionInternationale?: boolean

  @ApiPropertyOptional({ description: 'Section Européenne', type: Boolean })
  @IsOptional()
  sectionEuropeenne?: boolean

  @ApiPropertyOptional({ description: 'Restauration', type: Boolean })
  @IsOptional()
  restauration?: boolean

  @ApiPropertyOptional({ description: 'Hébergement', type: Boolean })
  @IsOptional()
  hebergement?: boolean

  @ApiPropertyOptional({
    description:
      "Effectif déclaré (donnée informative saisie manuellement, distincte du nombre réel de membres inscrits)",
    type: Number,
  })
  @IsOptional()
  @IsNumber()
  numberOfStudents?: number

  @ApiPropertyOptional({ description: 'UAI Parent', type: 'string' })
  @IsOptional()
  @IsString()
  parentUai?: string

  @ApiPropertyOptional({ description: 'Type Parent', type: 'string' })
  @IsOptional()
  @IsString()
  parentType?: string

  @ApiPropertyOptional({ description: 'Type contrat privé', type: 'string' })
  @IsOptional()
  @IsString()
  privateContractType?: string

  @ApiPropertyOptional({ description: 'Code contrat privé', type: 'string' })
  @IsOptional()
  @IsString()
  privateContractCode?: string

  @ApiPropertyOptional({ description: 'Code circonscription', type: 'string' })
  @IsOptional()
  @IsString()
  districtCode?: string

  @ApiPropertyOptional({ description: 'Libellé circonscription', type: 'string' })
  @IsOptional()
  @IsString()
  districtLabel?: string

  @ApiPropertyOptional({ description: 'Code bassin formation', type: 'string' })
  @IsOptional()
  @IsString()
  formationBasinCode?: string

  @ApiPropertyOptional({ description: 'Libellé bassin formation', type: 'string' })
  @IsOptional()
  @IsString()
  formationBasinLabel?: string

  @ApiPropertyOptional({ description: "Date d'ouverture" })
  @IsOptional()
  openedAt?: Date

  @ApiPropertyOptional({ description: 'Date MAJ officielle' })
  @IsOptional()
  officialUpdatedAt?: Date

  @ApiPropertyOptional({ description: 'Statut officiel', type: 'string' })
  @IsOptional()
  @IsString()
  officialStatus?: string

  @ApiPropertyOptional({ description: 'Ministère de tutelle', type: 'string' })
  @IsOptional()
  @IsString()
  ministry?: string

  @ApiPropertyOptional({ description: 'Latitude', type: Number })
  @IsOptional()
  @IsNumber()
  latitude?: number

  @ApiPropertyOptional({ description: 'Longitude', type: Number })
  @IsOptional()
  @IsNumber()
  longitude?: number
}
